import React, { useState, useEffect } from 'react';
import "../css/ViewStudents.css";
import { Search, Trash2, X, Filter, User, Mail, Phone, Hash, BookOpen, Building, Users, Calendar } from 'lucide-react';

function ViewStudents() {
    const [students, setStudents] = useState([]);
    const [activeTab, setActiveTab] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [studentToDelete, setStudentToDelete] = useState(null);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch students data from API
    useEffect(() => {
        fetchStudents();
    }, []);

    const fetchStudents = async () => {
        try {
            setIsLoading(true);
            const response = await fetch('https://payroll-api.uniums.com/api/students/students');
            
            if (!response.ok) {
                throw new Error('Failed to fetch students');
            }
            
            const data = await response.json();
            setStudents(data);
            setError(null);
        } catch (err) {
            setError('Error fetching students: ' + err.message);
            console.error('Error fetching students:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // Effect to filter students based on tab and search query
    useEffect(() => {
        // Simulate loading for smoother transitions
        setIsLoading(true);

        // Use setTimeout to prevent layout shifts
        const timer = setTimeout(() => {
            let result = [...students];

            // Filter by gender tab
            if (activeTab === 'boys') {
                result = result.filter(student => student.gender === 'male');
            } else if (activeTab === 'girls') {
                result = result.filter(student => student.gender === 'female');
            }

            // Filter by search query
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                result = result.filter(student =>
                    student.name.toLowerCase().includes(query) ||
                    student.rollNo.toLowerCase().includes(query) ||
                    student.department.toLowerCase().includes(query) ||
                    student.email.toLowerCase().includes(query)
                );
            }

            setFilteredStudents(result);
            setIsLoading(false);
        }, 300); // Short delay for transition effect

        return () => clearTimeout(timer);
    }, [students, activeTab, searchQuery]);

    // Handle delete confirmation
    const handleDeleteConfirm = async () => {
        if (studentToDelete) {
            try {
                setIsLoading(true);
                const response = await fetch(`https://payroll-api.uniums.com/api/students/delete-student/${studentToDelete.rollNo}`, {
                    method: 'DELETE',
                });
                
                const data = await response.json();
                
                if (!response.ok) {
                    throw new Error(data.message || 'Failed to delete student');
                }
                
                // Refresh the students list after deletion
                await fetchStudents();
                setShowModal(false);
                setStudentToDelete(null);
            } catch (err) {
                setError('Error deleting student: ' + err.message);
                console.error('Error deleting student:', err);
            } finally {
                setIsLoading(false);
            }
        }
    };

    // Function to handle delete click
    const handleDeleteClick = (student) => {
        setStudentToDelete(student);
        setShowModal(true);
    };

    // Function to close modal
    const closeModal = () => {
        setShowModal(false);
        setStudentToDelete(null);
    };

    // Handle tab change with smooth transition
    const handleTabChange = (tab) => {
        if (tab !== activeTab) {
            setActiveTab(tab);
        }
    };

    return (
        <div className="view-students-container">
            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}
            
            {/* Tab Navigation */}
            <div className="students-tabs">
                <button
                    className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
                    onClick={() => handleTabChange('all')}
                >
                    All Students
                </button>
                <button
                    className={`tab-button ${activeTab === 'boys' ? 'active' : ''}`}
                    onClick={() => handleTabChange('boys')}
                >
                    Boys
                </button>
                <button
                    className={`tab-button ${activeTab === 'girls' ? 'active' : ''}`}
                    onClick={() => handleTabChange('girls')}
                >
                    Girls
                </button>
            </div>

            {/* Search and Filter */}
            <div className="search-filter-container">
                <div className="search-box">
                    <Search size={20} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by name, roll number, department..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button className="clear-search" onClick={() => setSearchQuery('')}>
                            <X size={16} />
                        </button>
                    )}
                </div>

                <div className="filter-stats">
                    <Filter size={18} />
                    <span>{filteredStudents.length} students</span>
                </div>
            </div>

            {/* Content Area with fixed height */}
            <div className="content-area">
                {isLoading ? (
                    <div className="loading-spinner">
                        <div className="spinner"></div>
                    </div>
                ) : filteredStudents.length > 0 ? (
                    <div className="students-grid">
                        {filteredStudents.map(student => (
                            <div key={student._id || student.id || student.rollNo} className="student-profile-card">
                                <div className="student-profile-header">
                                    <div className="student-avatar" style={{ backgroundColor: getAvatarColor(student.name) }}>
                                        {student.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="student-basic-info">
                                        <h3>{student.name}</h3>
                                        <p className="student-id">Roll No: {student.rollNo}</p>
                                    </div>
                                    <button
                                        className="delete-button"
                                        onClick={() => handleDeleteClick(student)}
                                        aria-label="Delete student"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>

                                <div className="student-details-grid">
                                    <div className="detail-item">
                                        <Mail size={16} className="detail-icon" />
                                        <div>
                                            <span className="detail-label">Email</span>
                                            <span className="detail-value">{student.email}</span>
                                        </div>
                                    </div>

                                    <div className="detail-item">
                                        <Phone size={16} className="detail-icon" />
                                        <div>
                                            <span className="detail-label">Phone</span>
                                            <span className="detail-value">{student.phone}</span>
                                        </div>
                                    </div>

                                    <div className="detail-item">
                                        <BookOpen size={16} className="detail-icon" />
                                        <div>
                                            <span className="detail-label">Class</span>
                                            <span className="detail-value">{student.class}</span>
                                        </div>
                                    </div>

                                    <div className="detail-item">
                                        <Building size={16} className="detail-icon" />
                                        <div>
                                            <span className="detail-label">Department</span>
                                            <span className="detail-value">{student.department}</span>
                                        </div>
                                    </div>

                                    <div className="detail-item">
                                        <Users size={16} className="detail-icon" />
                                        <div>
                                            <span className="detail-label">Gender</span>
                                            <span className="detail-value">{capitalizeFirstLetter(student.gender)}</span>
                                        </div>
                                    </div>

                                    <div className="detail-item">
                                        <Calendar size={16} className="detail-icon" />
                                        <div>
                                            <span className="detail-label">Batch</span>
                                            <span className="detail-value">{student.batch}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="no-results">
                        <div className="no-results-icon">
                            <User size={48} />
                        </div>
                        <h3>No students found</h3>
                        <p>Try adjusting your search or filter criteria</p>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3>Confirm Deletion</h3>
                            <button className="close-modal" onClick={closeModal}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete the student record for:</p>
                            <p className="student-delete-name">{studentToDelete?.name}</p>
                            <p className="warning-text">This action cannot be undone.</p>
                        </div>
                        <div className="modal-footer">
                            <button className="cancel-button" onClick={closeModal}>
                                Cancel
                            </button>
                            <button className="delete-confirm-button" onClick={handleDeleteConfirm}>
                                Delete Student
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Helper function to capitalize first letter
function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

// Helper function to generate consistent avatar colors
function getAvatarColor(name) {
    const colors = [
        '#3498db', '#2ecc71', '#f1c40f', '#e74c3c', '#9b59b6',
        '#1abc9c', '#e67e22', '#34495e', '#d35400', '#16a085'
    ];

    // Simple hash function to generate a consistent color for a name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash % colors.length);
    return colors[index];
}

export default ViewStudents;